import {
  UsersIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface CEODashboardPageProps {
  onNavigate?: (page: string) => void;
}

interface GymStats {
  totalGyms: number;
  activeGyms: number;
  totalMembers: number;
  totalRevenue: number;
  recentEnrollments: number;
}

interface RecentActivity {
  id: string;
  type: 'gym_enrolled' | 'member_joined' | 'payment_received';
  description: string;
  timestamp: string;
  gymName?: string;
}

const CEODashboardPage: React.FC<CEODashboardPageProps> = ({ onNavigate }) => {
  const { user } = useAuth();
  const [stats, setStats] = useState<GymStats>({
    totalGyms: 0,
    activeGyms: 0,
    totalMembers: 0,
    totalRevenue: 0,
    recentEnrollments: 0
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Mock data for now - will be replaced with real API calls
      setStats({
        totalGyms: 12,
        activeGyms: 11,
        totalMembers: 1247,
        totalRevenue: 45680,
        recentEnrollments: 3
      });

      setRecentActivity([
        {
          id: '1',
          type: 'gym_enrolled',
          description: 'New gym enrolled',
          timestamp: '2025-01-19T10:30:00Z',
          gymName: 'FitLife Gym'
        },
        {
          id: '2',
          type: 'member_joined',
          description: '15 new members joined',
          timestamp: '2025-01-19T09:15:00Z',
          gymName: 'CrossFit Central'
        },
        {
          id: '3',
          type: 'payment_received',
          description: 'Monthly payment received',
          timestamp: '2025-01-19T08:45:00Z',
          gymName: 'PowerHouse Fitness'
        }
      ]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'gym_enrolled':
        return '🏋️‍♂️';
      case 'member_joined':
        return '👥';
      case 'payment_received':
        return '💰';
      default:
        return '📊';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-white">Loading CEO Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white">CEO Dashboard</h1>
        <p className="mt-2 text-gray-300">
          Welcome back, {user?.first_name}! Here's your business overview.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                <BuildingOfficeIcon className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-300">Total Gyms</p>
              <p className="text-2xl font-semibold text-white">{stats.totalGyms}</p>
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg flex items-center justify-center">
                <CheckCircleIcon className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-300">Active Gyms</p>
              <p className="text-2xl font-semibold text-white">{stats.activeGyms}</p>
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                <UsersIcon className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-300">Total Members</p>
              <p className="text-2xl font-semibold text-white">{stats.totalMembers.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                <CurrencyDollarIcon className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-300">Total Revenue</p>
              <p className="text-2xl font-semibold text-white">{formatCurrency(stats.totalRevenue)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20">
            <div className="px-6 py-4 border-b border-white/20">
              <h3 className="text-lg font-medium text-white">Recent Activity</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-gradient-to-r from-orange-500/20 to-blue-500/20 rounded-lg flex items-center justify-center">
                        <span className="text-lg">{getActivityIcon(activity.type)}</span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">
                        {activity.description}
                      </p>
                      {activity.gymName && (
                        <p className="text-sm text-gray-300">{activity.gymName}</p>
                      )}
                      <p className="text-xs text-gray-400">
                        {formatTimestamp(activity.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="space-y-6">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20">
            <div className="px-6 py-4 border-b border-white/20">
              <h3 className="text-lg font-medium text-white">Quick Actions</h3>
            </div>
            <div className="p-6 space-y-4">
              <button 
                onClick={() => onNavigate?.('enrollment')}
                className="w-full bg-gradient-to-r from-orange-500 to-blue-500 text-white px-4 py-2 rounded-lg hover:from-orange-600 hover:to-blue-600 transition-all duration-200 font-medium"
              >
                Enroll New Gym
              </button>
              <button 
                onClick={() => onNavigate?.('gyms')}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-4 py-2 rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 font-medium"
              >
                View All Gyms
              </button>
              <button 
                onClick={() => onNavigate?.('dashboard')}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-200 font-medium"
              >
                System Analytics
              </button>
            </div>
          </div>

          {/* System Health */}
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20">
            <div className="px-6 py-4 border-b border-white/20">
              <h3 className="text-lg font-medium text-white">System Health</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-300">API Status</span>
                <span className="text-sm font-medium text-green-400">Healthy</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-300">Database</span>
                <span className="text-sm font-medium text-green-400">Connected</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-300">Redis Cache</span>
                <span className="text-sm font-medium text-green-400">Active</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-300">File Storage</span>
                <span className="text-sm font-medium text-green-400">Available</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CEODashboardPage;
